import * as React from 'react';
import { Mail, MapPin, Paperclip } from 'lucide-react';
import { useLocale, useT } from '../lib/i18n.js';
import { formatViewingDay, formatViewingTime } from '../lib/viewing-format.js';
import { formatRelativeChange } from '../lib/relative-time.js';
import {
  mailExcerpt,
  mailPrimaryAction,
  mailReplyTemplates,
  type MailPrimaryAction,
  type MailReplyTemplate,
} from '../lib/mail-cards.js';
import type { MailCardDto } from '../lib/mailbox-api.js';
import type { ProposedEventDto, ReplyDraftDto } from '../lib/inquiry-ai-api.js';

export interface MailCardProps {
  readonly card: MailCardDto;
  readonly now: Date;
  /** Název zakázky pro štítek (spárovaná karta nebo kandidát „Vyber zakázku"). */
  readonly caseName: (caseId: string) => string | null;
  readonly busy: boolean;
  readonly onPrimary: (card: MailCardDto, action: MailPrimaryAction, chosenCaseId?: string) => void;
  readonly onResolve: (card: MailCardDto, action: 'done' | 'dismissed') => void;
  /**
   * Krok MAIL1 bod 2 — „Odpovědět". `canReply: false` (schránka bez odchozího
   * serveru) tlačítko vůbec nenabídne: nabídnout ho a pak odpověď odmítnout by
   * byla práce navíc pro nic.
   */
  readonly canReply?: boolean;
  readonly onReply?: (card: MailCardDto, text: string) => void;
  /** Hlášky po odeslání odpovědi (odesláno / vypnuto / limit). */
  readonly replyNote?: string | null;
  /**
   * Krok PROP5 bod 5 — „Uložit přílohu k zakázce". Bez handleru se tlačítko
   * vůbec nenabídne; `index` je pozice v `card.attachments` (tak si přílohu
   * najde server, názvy souborů se v jedné zprávě opakují).
   */
  readonly onSaveAttachment?: (card: MailCardDto, index: number) => void;
  /** Hláška po uložení přílohy (uloženo / už tam je / proč to nešlo). */
  readonly attachmentNote?: string | null;
  /**
   * Krok NAP4 bod A9 — „Navrhnout odpověď". Vrací KONCEPT do editoru
   * odpovědi; nic neodesílá (to zůstává na „Odeslat odpověď"). Bez handleru
   * se tlačítko vůbec nenabídne.
   */
  readonly onSuggestReply?: (card: MailCardDto) => Promise<ReplyDraftDto>;
  /** Krok NAP4 bod A9 — termíny navržené v e-mailu (návrhy, ne události). */
  readonly onProposedEvents?: (card: MailCardDto) => Promise<readonly ProposedEventDto[]>;
  /** Krok NAP4 bod A9 — z návrhu udělá událost v kalendáři (jeden klik makléře). */
  readonly onCreateEvent?: (card: MailCardDto, proposal: ProposedEventDto) => Promise<void>;
}

/**
 * Krok AS1 bod 5 — karta „Nové z e-mailu" (předloha `tabule/Ukoly.png`).
 *
 * Štítek druhu a čas, předmět, odesílatel, PROČ tu karta je (pravidlo, ne
 * AI — v tomto kroku žádná AI), zakázka, přílohy a JEDNA hlavní akce.
 * „Návrh odpovědi" z předlohy patří AS3 a tady není.
 */
export function MailCard({
  card,
  now,
  caseName,
  busy,
  onPrimary,
  onResolve,
  canReply = false,
  onReply,
  replyNote = null,
  onSaveAttachment,
  attachmentNote = null,
  onSuggestReply,
  onProposedEvents,
  onCreateEvent,
}: MailCardProps): React.JSX.Element {
  const t = useT();
  const locale = useLocale();
  const action = mailPrimaryAction(card);
  const [chosen, setChosen] = React.useState('');
  const [replyOpen, setReplyOpen] = React.useState(false);
  const [replyText, setReplyText] = React.useState('');
  // Krok NAP4 bod A9: stav AI pomoci u TÉTO karty. `proposals === null` =
  // termíny se ještě nehledaly (prázdné pole je odpověď „žádný termín").
  const [draftBusy, setDraftBusy] = React.useState(false);
  const [draftNote, setDraftNote] = React.useState<string | null>(null);
  const [proposals, setProposals] = React.useState<readonly ProposedEventDto[] | null>(null);
  const [eventsBusy, setEventsBusy] = React.useState(false);
  const [eventNote, setEventNote] = React.useState<string | null>(null);

  async function suggestReply(): Promise<void> {
    if (onSuggestReply === undefined) return;
    setDraftBusy(true);
    setDraftNote(null);
    try {
      const draft = await onSuggestReply(card);
      // Koncept jde DO EDITORU, odeslání zůstává vědomý druhý klik.
      if (draft.text !== null) setReplyText(draft.text);
      setDraftNote(draft.text !== null ? t('webAdmin.mailbox.aiDraft.hint') : draft.note);
    } catch (error) {
      setDraftNote(error instanceof Error ? error.message : t('webAdmin.mailbox.aiDraft.working'));
    } finally {
      setDraftBusy(false);
    }
  }

  async function loadProposals(): Promise<void> {
    if (onProposedEvents === undefined) return;
    setEventsBusy(true);
    setEventNote(null);
    try {
      setProposals(await onProposedEvents(card));
    } catch (error) {
      setProposals([]);
      setEventNote(error instanceof Error ? error.message : t('webAdmin.mailbox.proposedEvents.failed'));
    } finally {
      setEventsBusy(false);
    }
  }

  /** Den a čas návrhu v pásmu kanceláře — tentýž formát jako u prohlídek. */
  function formatProposal(proposal: ProposedEventDto): string {
    return `${formatViewingDay(proposal.startsAt, locale)}, ${formatViewingTime(proposal.startsAt, locale)}`;
  }

  async function createEvent(proposal: ProposedEventDto): Promise<void> {
    if (onCreateEvent === undefined) return;
    setEventsBusy(true);
    setEventNote(null);
    try {
      await onCreateEvent(card, proposal);
      setEventNote(t('webAdmin.mailbox.proposedEvents.created'));
    } catch (error) {
      setEventNote(error instanceof Error ? error.message : t('webAdmin.mailbox.proposedEvents.failed'));
    } finally {
      setEventsBusy(false);
    }
  }

  const relative = {
    today: (time: string) => time,
    yesterday: (_time: string) => t('webAdmin.mailbox.card.yesterday'),
  };
  const kindKey = card.chooseCase ? 'choose' : card.kind;
  const from = [card.fromName, card.fromEmail].filter((part): part is string => part !== null && part !== '').join(' · ');
  const linkedCase = card.caseId === null ? null : caseName(card.caseId);
  /*
   * Krok PROP5 bod 5: co je dokument, rozhodl SERVER (`isDocument`) — dřív to
   * tady bylo druhé pravidlo („není to obrázek"), které se s tím serverovým
   * rozcházelo u naskenované listiny poslané jako JPG.
   */
  const documents = card.attachments
    .map((attachment, index) => ({ attachment, index }))
    .filter(({ attachment }) => attachment.isDocument);

  return (
    <article className={`rh-a2-tcard rh-a2-mcard rh-a2-mcard--${kindKey}`} data-testid={`mail-card-${card.id}`} data-kind={card.kind}>
      <div className="rh-a2-mcard__top">
        <span className={`rh-a2-tag rh-a2-mtag rh-a2-mtag--${kindKey}`}>
          <Mail size={12} strokeWidth={2} aria-hidden="true" />
          {t(`webAdmin.mailbox.kind.${kindKey}` as 'webAdmin.mailbox.kind.poptavka')}
        </span>
        <span className="rh-a2-tcard__due">{formatRelativeChange(card.receivedAt, now, relative)}</span>
      </div>
      <div className="rh-a2-tcard__title rh-a2-mcard__title">{card.subject ?? t('webAdmin.mailbox.card.noSubject')}</div>
      {from !== '' ? <div className="rh-a2-meta rh-a2-mcard__from">{from}</div> : null}
      {/* Krok UXL1 bod 4: karta nese jen krátké (dvouřádkové) shrnutí; proč
          karta vznikla (zdůvodnění) a delší text e-mailu jsou až v detailu
          (panel). Celý e-mail REOS neukládá (jen `excerpt`) — detail proto
          ukazuje celý dostupný `excerpt`, ne zkrácenou verzi z karty. */}
      <div className="rh-a2-mcard__summary" data-testid={`mail-card-summary-${card.id}`}>
        {card.excerpt !== '' ? mailExcerpt(card.excerpt, 140) : t('webAdmin.mailbox.card.noExcerpt')}
      </div>
      <details className="rh-a2-mcard__detail" data-testid={`mail-card-detail-${card.id}`}>
        <summary>{t('webAdmin.mailbox.card.detailToggle')}</summary>
        <p data-testid={`mail-card-why-${card.id}`}>
          <strong>{t('webAdmin.mailbox.card.whyLabel')}</strong>{' '}
          {t(`webAdmin.mailbox.reason.${card.reason}` as 'webAdmin.mailbox.reason.matched')}
        </p>
        {card.excerpt !== '' ? <p className="rh-a2-mcard__full">{card.excerpt}</p> : null}
      </details>
      <div className="rh-a2-tcard__row">
        {linkedCase !== null ? (
          <span className="rh-a2-tag rh-a2-tag--case">
            <MapPin size={12} strokeWidth={2} aria-hidden="true" />
            <span>{linkedCase}</span>
          </span>
        ) : null}
        {documents.length > 0 ? (
          <span className="rh-a2-tag rh-a2-tag--non">
            <Paperclip size={12} strokeWidth={2} aria-hidden="true" />
            {t('webAdmin.mailbox.card.attachments', { count: documents.length })}
          </span>
        ) : null}
        {card.portal !== null ? (
          <span className="rh-a2-tag rh-a2-tag--non">{t(`webAdmin.mailbox.portal.${card.portal}` as 'webAdmin.mailbox.portal.sreality')}</span>
        ) : null}
      </div>
      {/* Krok PROP5 bod 5 (nález 21): příloha spárovaného e-mailu se dá uložit
          k zakázce. Bajty tahá server ze schránky (jen ke čtení) a žene je
          antivirem — tady je jen volba, kterou přílohu. */}
      {onSaveAttachment !== undefined && card.caseId !== null && documents.length > 0 ? (
        <div className="rh-a2-mcard__files" data-testid={`mail-card-attachments-${card.id}`}>
          <span className="rh-a2-meta">{t('webAdmin.mailbox.card.attachmentsHeading')}</span>
          <ul>
            {documents.map(({ attachment, index }) => (
              <li key={`${String(index)}-${attachment.filename}`}>
                <span>{attachment.filename}</span>
                <button
                  type="button"
                  className="rh-a2-btn rh-a2-btn--sm"
                  disabled={busy}
                  data-testid={`mail-card-attachment-save-${card.id}-${String(index)}`}
                  onClick={() => onSaveAttachment(card, index)}
                >
                  {t('webAdmin.mailbox.action.saveAttachment')}
                </button>
              </li>
            ))}
          </ul>
          {attachmentNote !== null ? (
            <p className="rh-a2-meta" role="status" data-testid={`mail-card-attachment-note-${card.id}`}>
              {attachmentNote}
            </p>
          ) : null}
        </div>
      ) : null}
      {action.kind === 'choose_case' ? (
        <label className="rh-a2-mcard__choose">
          <span className="rh-a2-meta">{t('webAdmin.mailbox.card.chooseLabel')}</span>
          <select value={chosen} onChange={(e) => setChosen(e.target.value)} data-testid={`mail-card-choose-${card.id}`}>
            <option value="">{t('webAdmin.mailbox.card.choosePlaceholder')}</option>
            {action.candidates.map((id) => (
              <option key={id} value={id}>
                {caseName(id) ?? id.slice(0, 8)}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <div className="rh-a2-tcard__act rh-a2-mcard__act">
        {action.kind !== 'done' ? (
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--sm rh-a2-btn--primary"
            disabled={busy || (action.kind === 'choose_case' && chosen === '')}
            data-testid={`mail-card-primary-${card.id}`}
            onClick={() => onPrimary(card, action, chosen === '' ? undefined : chosen)}
          >
            {t(`webAdmin.mailbox.action.${action.kind}` as 'webAdmin.mailbox.action.create_demand')}
          </button>
        ) : null}
        <button
          type="button"
          className="rh-a2-btn rh-a2-btn--sm"
          disabled={busy}
          data-testid={`mail-card-done-${card.id}`}
          onClick={() => onResolve(card, 'done')}
        >
          {t('webAdmin.mailbox.action.done')}
        </button>
        {canReply && card.fromEmail !== null && card.fromEmail !== '' ? (
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--sm"
            disabled={busy}
            aria-expanded={replyOpen}
            data-testid={`mail-card-reply-open-${card.id}`}
            onClick={() => setReplyOpen((open) => !open)}
          >
            {t('webAdmin.mailbox.action.reply')}
          </button>
        ) : null}
        <button
          type="button"
          className="rh-a2-link rh-a2-mcard__dismiss"
          disabled={busy}
          data-testid={`mail-card-dismiss-${card.id}`}
          onClick={() => onResolve(card, 'dismissed')}
        >
          {t('webAdmin.mailbox.action.dismiss')}
        </button>
      </div>
      {/* Krok MAIL1 bod 2: prostý text, odeslání z vlastní schránky makléře,
          vlákno drží server (`In-Reply-To`). Žádné přílohy, žádné HTML. */}
      {replyOpen ? (
        <div className="rh-a2-mcard__reply" data-testid={`mail-card-reply-${card.id}`}>
          <label className="rh-a2-meta" htmlFor={`mail-reply-${card.id}`}>
            {t('webAdmin.mailbox.card.replyTo', { address: card.fromEmail ?? '' })}
          </label>
          {/* Krok P1EXP2 bod 3: jedním klikem se vloží text šablony. ODESLÁNÍ
              zůstává vědomé (tlačítko „Odeslat odpověď") — dopis klientovi je
              nevratný a jediné omylem trefené tlačítko ho nesmí poslat. */}
          {/* Krok NAP4 bod A9: „Navrhnout odpověď" vloží KONCEPT do stejného
              editoru jako šablony. Model nic neodesílá — odeslání je pořád
              jen tlačítko „Odeslat odpověď" níž. */}
          {onSuggestReply !== undefined ? (
            <div className="rh-a2-mcard__tpl" data-testid={`mail-card-ai-draft-${card.id}`}>
              <button
                type="button"
                className="rh-a2-btn rh-a2-btn--sm"
                disabled={busy || draftBusy}
                data-testid={`mail-card-reply-draft-${card.id}`}
                onClick={() => void suggestReply()}
              >
                {draftBusy ? t('webAdmin.mailbox.aiDraft.working') : t('webAdmin.mailbox.action.replyDraft')}
              </button>
              {draftNote !== null ? (
                <p className="rh-a2-meta" role="status" data-testid={`mail-card-reply-draft-note-${card.id}`}>
                  {draftNote}
                </p>
              ) : null}
            </div>
          ) : null}
          <div className="rh-a2-mcard__tpl" data-testid={`mail-card-reply-templates-${card.id}`}>
            <span className="rh-a2-meta">{t('webAdmin.mailbox.replyTemplate.heading')}</span>
            {mailReplyTemplates(card).map((key: MailReplyTemplate) => (
              <button
                key={key}
                type="button"
                className="rh-a2-btn rh-a2-btn--sm"
                disabled={busy}
                data-testid={`mail-card-reply-template-${key}-${card.id}`}
                onClick={() => setReplyText(t(`webAdmin.mailbox.replyTemplate.body.${key}` as 'webAdmin.mailbox.replyTemplate.body.callback'))}
              >
                {t(`webAdmin.mailbox.replyTemplate.${key}` as 'webAdmin.mailbox.replyTemplate.callback')}
              </button>
            ))}
          </div>
          <textarea
            id={`mail-reply-${card.id}`}
            rows={4}
            value={replyText}
            maxLength={5000}
            onChange={(e) => setReplyText(e.target.value)}
            data-testid={`mail-card-reply-text-${card.id}`}
          />
          <button
            type="button"
            className="rh-a2-btn rh-a2-btn--sm rh-a2-btn--primary"
            disabled={busy || replyText.trim() === ''}
            data-testid={`mail-card-reply-send-${card.id}`}
            onClick={() => onReply?.(card, replyText.trim())}
          >
            {t('webAdmin.mailbox.action.replySend')}
          </button>
        </div>
      ) : null}
      {replyNote !== null ? (
        <p className="rh-a2-meta" role="status" data-testid={`mail-card-reply-note-${card.id}`}>
          {replyNote}
        </p>
      ) : null}
      {/* Krok NAP4 bod A9: termín napsaný v e-mailu („můžeme v úterý v 17?")
          je NÁVRH. Událost v kalendáři vzniká až klikem makléře — datum a
          čas počítá server v pásmu kanceláře (`startsAt`). */}
      {onProposedEvents !== undefined ? (
        <div className="rh-a2-mcard__events" data-testid={`mail-card-proposed-events-${card.id}`}>
          <span className="rh-a2-meta">{t('webAdmin.mailbox.proposedEvents.heading')}</span>
          {proposals === null ? (
            <button
              type="button"
              className="rh-a2-btn rh-a2-btn--sm"
              disabled={busy || eventsBusy}
              data-testid={`mail-card-proposed-events-load-${card.id}`}
              onClick={() => void loadProposals()}
            >
              {eventsBusy ? t('webAdmin.mailbox.proposedEvents.working') : t('webAdmin.mailbox.proposedEvents.load')}
            </button>
          ) : proposals.length === 0 ? (
            <p className="rh-a2-meta" data-testid={`mail-card-proposed-events-none-${card.id}`}>
              {t('webAdmin.mailbox.proposedEvents.none')}
            </p>
          ) : (
            <ul>
              {proposals.map((proposal) => (
                <li key={`${proposal.date}-${String(proposal.hour)}-${String(proposal.minute)}`}>
                  <span data-testid={`mail-card-proposed-event-${card.id}-${proposal.date}`}>
                    {formatProposal(proposal)}
                  </span>{' '}
                  <span className="rh-a2-meta">{t('webAdmin.mailbox.proposedEvents.quote', { quote: proposal.sourceQuote })}</span>
                  {onCreateEvent !== undefined ? (
                    <button
                      type="button"
                      className="rh-a2-btn rh-a2-btn--sm"
                      disabled={busy || eventsBusy}
                      data-testid={`mail-card-proposed-event-create-${card.id}-${proposal.date}`}
                      onClick={() => void createEvent(proposal)}
                    >
                      {t('webAdmin.mailbox.proposedEvents.create')}
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {eventNote !== null ? (
            <p className="rh-a2-meta" role="status" data-testid={`mail-card-proposed-events-note-${card.id}`}>
              {eventNote}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
